import { useCallback, useState } from 'react'

/**
 * The idempotency key a posting carries.
 *
 * The key is what makes a retry after a timeout safe: the server matches it and answers with the
 * posting it already made, flagged `wasReplay`, instead of taking the money twice.
 *
 * **One key per submission, never one per mounted form.** The server's replay lookup matches on
 * the key alone — not the account, not the transaction type, not the amount — so a key that
 * outlives the payment it belongs to will match the *next* payment keyed into the same form. The
 * screen would then report "already posted" with the previous customer's transaction number, and
 * the cash just taken would never be credited. A teller screen keeps its form mounted after a
 * success precisely so the receipt can be read back, which is exactly the shape that fails.
 *
 * So: hold one key across retries of a single attempt, and mint a new one the moment an attempt
 * settles into a real posting. {@link ClientOperationId.spend} is that moment, and it is called on
 * every non-replay success — including a queued dual-controlled withdrawal, whose key is already
 * recorded in the stored approval payload.
 */
export interface ClientOperationId {
  /** The key to send with the next attempt. Stable across retries of that attempt. */
  current: string
  /** Consumes the key and mints the next one. Call on any success that was not a replay. */
  spend: () => void
  /** Throws the current key away without treating it as spent — for a reset or a cancel. */
  reset: () => void
}

/**
 * The key has to be a **syntactically valid UUID**, not merely unique.
 *
 * `clientOperationId` is declared `format: uuid` in the contract and binds to a .NET `Guid?`, so a
 * key the server cannot parse is not a weaker key — it is a 400 on the posting, before anything is
 * validated. `crypto.randomUUID` is only defined in a secure context, and a branch back office
 * reached over plain http on a LAN is not one, so the fallback has to produce a real RFC-4122 v4
 * rather than something UUID-shaped. `crypto.getRandomValues` *is* available without a secure
 * context and is used where it is; `Math.random` is the last resort, and uniqueness within one
 * counter's day is the whole requirement of it.
 */
const mint = (): string => {
  // Typed loosely on purpose: the whole point is the runtime where one of these is absent, and
  // `lib.dom` insists both are always there.
  const web: Partial<Crypto> | undefined = typeof crypto === 'undefined' ? undefined : crypto

  if (typeof web?.randomUUID === 'function') {
    return web.randomUUID()
  }

  const bytes = new Uint8Array(16)

  if (typeof web?.getRandomValues === 'function') {
    web.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }

  // Version 4, variant 10xx — the two nibbles .NET's parser does not care about but every other
  // reader of this key does.
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function useClientOperationId(): ClientOperationId {
  const [current, setCurrent] = useState(mint)

  // `spend` and `reset` do the same thing, and they are two names on purpose: one records that a
  // payment consumed the key, the other that the form was abandoned. Minting an extra key nobody
  // sends costs nothing; sending one twice costs a customer their deposit.
  const spend = useCallback(() => {
    setCurrent(mint())
  }, [])

  const reset = spend

  return { current, spend, reset }
}
